import React, { createContext, useContext, useEffect, useState } from 'react'
import { useFetchDiscountCode } from '../api/queries/useFetchDiscountByCode'
import { useFetchDefaultDiscount } from 'api/queries/useFetchDefaultDiscount'
import { Storage, StorageKeys } from '../services/Storage'
import { formatCurrencyWithoutDecimals } from '../utils/currency'
import { popElementFromParams, existsInParamsCheck } from 'utils/queryParams'
import { useLocationSearch } from 'hooks/useRouter'
import { BLACK_FRIDAY_DISCOUNT_CODE } from 'models/Discount'
import { isFeatureActive } from '../services/TimeBasedFeatures'

interface DiscountCodeContextProps {
  discountCodeData: any
  defaultDiscountCode: string
  isLoading: boolean
  error: any
  refetchDiscountCode: () => void
}

type MyComponentProps = {
  children: React.ReactNode
}

const DiscountCodeContext = createContext<DiscountCodeContextProps | undefined>(undefined)

export const DiscountCodeProvider: React.FC<MyComponentProps> = ({ children }) => {
  const search = useLocationSearch()
  const [discountCodeData, setDiscountCodeData] = useState<any>(null)
  const [defaultDiscountCode, setDefaultDiscountCode] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<any>(null)
  const code = Storage.get(StorageKeys.PROMO_CODE) ?? null
  const { data, status, refetch } = useFetchDiscountCode(code, {
    enabled: !!code,
  })
  const { data: defaultDiscount, status: statusDefaultDiscount } = useFetchDefaultDiscount()
  const promotionCode = BLACK_FRIDAY_DISCOUNT_CODE

  const formatForDisplay = (data: any) => {
    const discountTreatQuantity = twoTreatDiscounts.includes(code) ? 2 : 1

    const treatCodeCopy = code.toLowerCase().includes('bison')
      ? `bison treats ($${27.99 * discountTreatQuantity} value)!`
      : code.toLowerCase().includes('pork')
      ? `pork treats ($${23.99 * discountTreatQuantity} value)!`
      : undefined

    const freeCopyQuantity = discountTreatQuantity === 2 ? '2 FREE bags' : 'a FREE bag'

    let displayFreeCopy = treatCodeCopy
      ? `PLUS, ${freeCopyQuantity} of all-natural ${treatCodeCopy}`
      : '+ Free Shipping!'

    const discountType = data.type

    const displayAmount =
      discountType === 'PERCENTAGE'
        ? `${data.amount}%`
        : `${formatCurrencyWithoutDecimals(data.amount)}`

    const discountDuration = data.subscriptionChargeLimit
    const boxSpelling = data.subscriptionChargeLimit === 1 ? 'box' : 'boxes'
    const durationTotalAmount =
      discountDuration && formatCurrencyWithoutDecimals(data.amount * discountDuration)

    let displayText = ''

    if (data.channel?.toLowerCase().includes('free_referral')) {
      displayText = `Redeem Your First FREE Box`
      displayFreeCopy = '+ FREE Shipping!'
    } else if (!!discountDuration && discountType === 'PERCENTAGE') {
      displayText = `Get ${displayAmount} off your first ${discountDuration} ${boxSpelling}`
    } else if (!!discountDuration && discountType !== 'FIXED') {
      displayText = `Get ${durationTotalAmount} off! That's ${displayAmount} off ${discountDuration} ${boxSpelling}`
    } else if (!!discountDuration && discountType === 'FIXED') {
      displayText = `Get ${discountDuration} ${boxSpelling} for only ${durationTotalAmount}! That's ${displayAmount} per box`
    } else if (discountType === 'DIRECT') {
      displayText = `Get ${displayAmount} off your first box`
    } else if (discountType === 'FIXED') {
      displayText = `Introducing the ${displayAmount} Trial Box`
    } else if (discountType === 'PERCENTAGE') {
      displayText = `Get ${displayAmount} off your first box`
    }
    return { ...data, displayText, displayAmount, displayFreeCopy }
  }

  useEffect(() => {
    //checks if the current date is within the promotion period and sets the promo code accordingly
    const setPromoCodeBasedOnDate = () => {
      return isFeatureActive('blackFridayPromo') ? promotionCode : defaultDiscount?.code
    }

    //checks if a promo code exists in the query params (url) and sets the promo code to what is there
    if (existsInParamsCheck('promocode', search)) {
      let pc = popElementFromParams('promoCode', search)
      if (pc) {
        Storage.set(StorageKeys.PROMO_CODE, pc)
      }
    } else {
      //if no promo code in query params, check if there is one in storage
      const currentCode = Storage.get(StorageKeys.PROMO_CODE)

      // if no promo code in storage check promotion
      if (
        !currentCode ||
        (currentCode === promotionCode && !isFeatureActive('blackFridayPromo')) ||
        (currentCode === defaultDiscount?.code && isFeatureActive('blackFridayPromo'))
      ) {
        // if the current date is within the promotion period set the promo code accordingly, else set to default code
        Storage.set(StorageKeys.PROMO_CODE, setPromoCodeBasedOnDate())
      }
    }
  }, [defaultDiscount, promotionCode, search])

  useEffect(() => {
    setIsLoading(status === 'loading' || statusDefaultDiscount === 'loading')
    if (status === 'error' || statusDefaultDiscount === 'error') {
      setError(error)
    } else if (status === 'success' && statusDefaultDiscount === 'success') {
      const formattedData = formatForDisplay(data)
      setDiscountCodeData(formattedData)
      setDefaultDiscountCode(defaultDiscount.code)
    }
  }, [data, status, statusDefaultDiscount, error])

  const refetchDiscountCode = () => {
    refetch()
  }

  return (
    <DiscountCodeContext.Provider
      value={{ discountCodeData, defaultDiscountCode, isLoading, error, refetchDiscountCode }}
    >
      {children}
    </DiscountCodeContext.Provider>
  )
}

export const useDiscountCode = () => {
  const context = useContext(DiscountCodeContext)
  if (!context) {
    throw new Error('useDiscountCode must be used within a DiscountCodeProvider')
  }
  return context
}

const twoTreatDiscounts = ['35PORK', '40PORK', 'PORK40']
